import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import {
  ArrowRight, CheckCircle2,
  Code, ArrowDown, Zap, FileText, Shield, Key,
  RefreshCw, Terminal, Lock, Mail, BookOpen, Copy, Check
} from 'lucide-react';

/* ─── scroll-reveal ─── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add('revealed'); obs.unobserve(el); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function Reveal({ children, className = '', delay = 0 }) {
  const ref = useReveal();
  return (
    <div ref={ref} className={`reveal-el ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function SectionTag({ children }) {
  return (
    <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full text-[#2563EB] text-sm font-medium mb-4">
      {children}
    </span>
  );
}

/* ─── API Capability Card ─── */
function CapabilityCard({ icon: Icon, title, description, delay = 0 }) {
  return (
    <Reveal delay={delay}>
      <div className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-[#2563EB]/30 hover:shadow-lg transition-all" data-testid={`capability-${title.slice(0, 15).toLowerCase().replace(/\s/g, '-')}`}>
        <div className="w-12 h-12 rounded-xl bg-[#2563EB]/10 flex items-center justify-center mb-4">
          <Icon className="w-6 h-6 text-[#2563EB]" />
        </div>
        <h3 className="font-semibold text-slate-900 mb-2">{title}</h3>
        <p className="text-slate-600 text-sm leading-relaxed">{description}</p>
      </div>
    </Reveal>
  );
}

/* ─── API Endpoint Card ─── */
function EndpointCard({ method, endpoint, description, delay = 0 }) {
  const [copied, setCopied] = useState(false);
  
  const methodColors = {
    GET: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    POST: 'bg-blue-100 text-blue-700 border-blue-200',
    PUT: 'bg-amber-100 text-amber-700 border-amber-200',
    DELETE: 'bg-red-100 text-red-700 border-red-200',
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(endpoint);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Reveal delay={delay}>
      <div className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-[#2563EB]/30 hover:shadow-lg transition-all" data-testid={`endpoint-${method.toLowerCase()}-${endpoint.split('/').pop()}`}>
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-lg text-xs font-bold border ${methodColors[method]}`}>
              {method}
            </span>
            <code className="text-sm font-mono text-slate-700 bg-slate-50 px-3 py-1 rounded-lg">
              {endpoint}
            </code>
          </div>
          <button 
            onClick={handleCopy}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            title="Copy endpoint"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-slate-400" />}
          </button>
        </div>
        <p className="text-slate-600 text-sm leading-relaxed">{description}</p>
      </div>
    </Reveal>
  );
}

/* ─── Auth Requirement Card ─── */
function AuthCard({ icon: Icon, title, description, delay = 0 }) {
  return (
    <Reveal delay={delay}>
      <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl" data-testid={`auth-${title.toLowerCase().replace(/\s/g, '-')}`}>
        <div className="w-10 h-10 rounded-lg bg-[#2563EB]/10 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-[#2563EB]" />
        </div>
        <div>
          <h4 className="font-semibold text-slate-900 mb-1">{title}</h4>
          <p className="text-slate-600 text-sm">{description}</p>
        </div>
      </div>
    </Reveal>
  );
}

/* ─── Protected Content Overlay ─── */
function ProtectedContent({ title, description, children }) {
  return (
    <div className="relative">
      {/* Blurred content */}
      <div className="filter blur-sm pointer-events-none select-none opacity-50">
        {children}
      </div>
      
      {/* Login overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-2xl">
        <div className="text-center p-8 max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">{title}</h3>
          <p className="text-slate-600 text-sm mb-6">{description}</p>
          <Link 
            to="/contact" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl font-semibold transition-all"
          >
            <Key className="w-4 h-4" />
            Request API Access
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════ DEVELOPERS PAGE ═══════════════════════ */
const Developers = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // API Capabilities
  const capabilities = [
    {
      icon: FileText,
      title: 'Create e-Invoices',
      description: 'Generate Malaysia LHDN compliant e-Invoices from booking transactions automatically.'
    },
    {
      icon: RefreshCw,
      title: 'Check Invoice Status',
      description: 'Query the real-time status of invoices submitted to the MyInvois system.'
    },
    {
      icon: Zap,
      title: 'Cancel or Refund',
      description: 'Process invoice cancellations and refunds through the LHDN system.'
    },
    {
      icon: BookOpen,
      title: 'Retrieve Records',
      description: 'Access invoice records for reporting, reconciliation, and audit purposes.'
    },
  ];

  // API Endpoints
  const endpoints = [
    {
      method: 'POST',
      endpoint: '/api/invoices/create',
      description: 'Create a new e-Invoice from booking data. Accepts guest details, charges, and payment information to generate a compliant invoice.'
    },
    {
      method: 'GET',
      endpoint: '/api/invoices/status/{invoice_id}',
      description: 'Retrieve the current status of an e-Invoice submitted to MyInvois. Returns validation status, UUID, and any error messages.'
    },
    {
      method: 'POST',
      endpoint: '/api/invoices/cancel',
      description: 'Cancel an existing e-Invoice. Submits a cancellation request to LHDN and updates the invoice status.'
    },
    {
      method: 'GET',
      endpoint: '/api/invoices/list',
      description: 'Retrieve a paginated list of invoices with filters for date range, status, and property. Ideal for reporting and reconciliation.'
    },
  ];

  // Authentication requirements
  const authRequirements = [
    {
      icon: Key,
      title: 'API Key',
      description: 'A unique API key provided upon partner registration. Include in request headers.'
    },
    {
      icon: Lock,
      title: 'Secure HTTPS',
      description: 'All API requests must be made over HTTPS. HTTP requests will be rejected.'
    },
    {
      icon: Shield,
      title: 'Partner Credentials',
      description: 'Authentication credentials tied to your organization for audit and compliance.'
    },
  ];

  return (
    <>
      <SEO
        title="StayMii Developer API | Malaysia e-Invoice Integration for PMS & Booking Platforms"
        description="StayMii API enables hospitality platforms to automatically generate Malaysia LHDN compliant e-Invoices. Integrate your PMS or booking system with MyInvois."
        keywords="StayMii API, Malaysia e-invoice API, MyInvois integration, PMS API integration, hotel booking API, LHDN e-invoice developer"
        canonicalUrl="/developers"
      />

      <style>{`
        .reveal-el { opacity:0; transform:translateY(24px); transition: opacity .6s cubic-bezier(.16,1,.3,1), transform .6s cubic-bezier(.16,1,.3,1); }
        .reveal-el.revealed { opacity:1; transform:translateY(0); }
        .flow-arrow { animation: flowPulse 2s ease-in-out infinite; }
        @keyframes flowPulse { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
      `}</style>

      <div className="min-h-screen bg-white" data-testid="developers-page">

        {/* ═══════ NAV ═══════ */}
        <LandingNavBar />

        {/* ═══════ HERO ═══════ */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" data-testid="developers-hero">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A]" />
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Reveal>
              <SectionTag><Code className="h-4 w-4" /> Developer API</SectionTag>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                StayMii Developer API
              </h1>
              <p className="text-lg lg:text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
                The StayMii API allows hospitality platforms such as property management systems (PMS), channel managers, and booking platforms to automatically generate Malaysia LHDN compliant e-Invoices using booking and transaction data. Partners can integrate StayMii to automate tax compliance and invoice reporting through the MyInvois system.
              </p>
            </Reveal>
          </div>
        </section>

        {/* ═══════ API OVERVIEW ═══════ */}
        <section className="py-16 lg:py-24 bg-slate-50" data-testid="api-overview-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-12">
                <SectionTag><Terminal className="h-4 w-4" /> API Overview</SectionTag>
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">What You Can Build</h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  StayMii provides RESTful APIs that allow hospitality platforms to integrate e-invoice capabilities seamlessly.
                </p>
              </div>
            </Reveal>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {capabilities.map((cap, i) => (
                <CapabilityCard key={cap.title} {...cap} delay={i * 80} />
              ))}
            </div>
          </div>
        </section>

        {/* ═══════ AUTHENTICATION ═══════ */}
        <section className="py-16 lg:py-24 bg-white" data-testid="authentication-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-12">
                <SectionTag><Lock className="h-4 w-4" /> Authentication</SectionTag>
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Secure API Access</h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  All API requests require proper authentication to ensure secure and compliant data handling.
                </p>
              </div>
            </Reveal>

            <div className="bg-slate-100 rounded-3xl p-8 lg:p-10">
              <div className="grid md:grid-cols-1 gap-4">
                {authRequirements.map((auth, i) => (
                  <AuthCard key={auth.title} {...auth} delay={i * 80} />
                ))}
              </div>

              <Reveal delay={300}>
                <div className="mt-8 p-6 bg-[#1E293B] rounded-2xl">
                  <p className="text-xs text-slate-400 font-mono mb-2">Example Request Header</p>
                  <code className="text-sm text-emerald-400 font-mono block overflow-x-auto">
                    <span className="text-slate-400">Authorization:</span> Bearer YOUR_API_KEY<br />
                    <span className="text-slate-400">Content-Type:</span> application/json<br />
                    <span className="text-slate-400">X-Partner-ID:</span> your_partner_id
                  </code>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ═══════ API ENDPOINTS ═══════ */}
        <section className="py-16 lg:py-24 bg-slate-50" data-testid="endpoints-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-12">
                <SectionTag><Code className="h-4 w-4" /> API Endpoints</SectionTag>
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Available Endpoints</h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Core API endpoints for e-invoice management and reporting.
                </p>
              </div>
            </Reveal>

            <div className="space-y-4">
              {endpoints.map((ep, i) => (
                <EndpointCard key={ep.endpoint} {...ep} delay={i * 80} />
              ))}
            </div>
          </div>
        </section>

        {/* ═══════ PROTECTED SECTIONS ═══════ */}
        <section className="py-16 lg:py-24 bg-white" data-testid="protected-sections">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-12">
                <SectionTag><Lock className="h-4 w-4" /> Protected Resources</SectionTag>
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Developer Resources</h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Access API keys, detailed documentation, webhooks, and sandbox credentials after registration.
                </p>
              </div>
            </Reveal>

            <div className="grid md:grid-cols-2 gap-6">
              {/* API Keys */}
              <ProtectedContent 
                title="API Keys" 
                description="Login required to view and manage your API keys and credentials."
              >
                <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 h-48">
                  <div className="flex items-center gap-3 mb-4">
                    <Key className="w-6 h-6 text-slate-400" />
                    <h4 className="font-semibold text-slate-700">API Keys & Credentials</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                    <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                    <div className="h-4 bg-slate-200 rounded w-2/3"></div>
                  </div>
                </div>
              </ProtectedContent>

              {/* Full Request/Response Examples */}
              <ProtectedContent 
                title="Request/Response Examples" 
                description="Login required to view full API request and response documentation."
              >
                <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 h-48">
                  <div className="flex items-center gap-3 mb-4">
                    <Code className="w-6 h-6 text-slate-400" />
                    <h4 className="font-semibold text-slate-700">Full API Examples</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-full"></div>
                    <div className="h-4 bg-slate-200 rounded w-4/5"></div>
                    <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                  </div>
                </div>
              </ProtectedContent>

              {/* Webhooks */}
              <ProtectedContent 
                title="Webhooks" 
                description="Login required to configure and manage webhook endpoints."
              >
                <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 h-48">
                  <div className="flex items-center gap-3 mb-4">
                    <RefreshCw className="w-6 h-6 text-slate-400" />
                    <h4 className="font-semibold text-slate-700">Webhook Configuration</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-2/3"></div>
                    <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                    <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                  </div>
                </div>
              </ProtectedContent>

              {/* Sandbox Credentials */}
              <ProtectedContent 
                title="Sandbox Credentials" 
                description="Login required to access sandbox environment and test credentials."
              >
                <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 h-48">
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className="w-6 h-6 text-slate-400" />
                    <h4 className="font-semibold text-slate-700">Sandbox Environment</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-full"></div>
                    <div className="h-4 bg-slate-200 rounded w-2/3"></div>
                    <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                  </div>
                </div>
              </ProtectedContent>
            </div>
          </div>
        </section>

        {/* ═══════ INTEGRATION WORKFLOW ═══════ */}
        <section className="py-16 lg:py-24 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A]" data-testid="workflow-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-12">
                <SectionTag><RefreshCw className="h-4 w-4" /> How It Works</SectionTag>
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Integration Workflow</h2>
                <p className="text-lg text-white/70 max-w-2xl mx-auto">
                  See how your platform connects to StayMii and the LHDN MyInvois system.
                </p>
              </div>
            </Reveal>

            {/* Flow Diagram */}
            <Reveal delay={100}>
              <div className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-8 lg:p-12">
                <div className="flex flex-col items-center space-y-6">
                  
                  {/* Step 1: Booking Platform / PMS */}
                  <div className="w-full max-w-md">
                    <div className="bg-white rounded-2xl p-6 shadow-xl">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                          <Terminal className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">Booking Platform / PMS</h3>
                          <p className="text-sm text-slate-500">Your hospitality system</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Arrow Down */}
                  <div className="flex flex-col items-center flow-arrow">
                    <ArrowDown className="w-8 h-8 text-[#2563EB]" />
                    <span className="text-xs text-white/50 mt-1">API Request</span>
                  </div>

                  {/* Step 2: StayMii API */}
                  <div className="w-full max-w-md">
                    <div className="bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] rounded-2xl p-6 shadow-xl">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                          <Code className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-white">StayMii API</h3>
                          <p className="text-sm text-white/70">e-Invoice Processing Engine</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Arrow Down */}
                  <div className="flex flex-col items-center flow-arrow">
                    <ArrowDown className="w-8 h-8 text-[#2563EB]" />
                    <span className="text-xs text-white/50 mt-1">Submit to LHDN</span>
                  </div>

                  {/* Step 3: LHDN MyInvois */}
                  <div className="w-full max-w-md">
                    <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-emerald-200">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                          <FileText className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">LHDN MyInvois System</h3>
                          <p className="text-sm text-slate-500">Official validation & storage</p>
                        </div>
                        <CheckCircle2 className="w-6 h-6 text-emerald-500 ml-auto" />
                      </div>
                    </div>
                  </div>

                  {/* Arrow Down */}
                  <div className="flex flex-col items-center flow-arrow">
                    <ArrowDown className="w-8 h-8 text-[#2563EB]" />
                    <span className="text-xs text-white/50 mt-1">Status Response</span>
                  </div>

                  {/* Step 4: Status Returned */}
                  <div className="w-full max-w-md">
                    <div className="bg-white rounded-2xl p-6 shadow-xl">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                          <RefreshCw className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">Invoice Status Returned</h3>
                          <p className="text-sm text-slate-500">UUID, QR code, validation status</p>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═══════ DEVELOPER SUPPORT ═══════ */}
        <section className="py-16 lg:py-24 bg-slate-50" data-testid="support-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-12">
                <SectionTag><Mail className="h-4 w-4" /> Developer Support</SectionTag>
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Need Help?</h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Our developer support team is here to help you integrate successfully.
                </p>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <div className="bg-white rounded-3xl border border-slate-200 p-8 lg:p-12 text-center">
                <div className="w-16 h-16 rounded-2xl bg-[#2563EB]/10 flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-8 h-8 text-[#2563EB]" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Contact Developer Support</h3>
                <p className="text-slate-600 mb-6">For API documentation, integration questions, or technical support</p>
                <a 
                  href="mailto:developer@staymii.com" 
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl font-semibold transition-all hover:shadow-lg"
                  data-testid="developer-email-btn"
                >
                  <Mail className="w-5 h-5" />
                  developer@staymii.com
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═══════ CTA ═══════ */}
        <section className="py-20 lg:py-28 bg-gradient-to-br from-[#2563EB] via-[#1D4ED8] to-[#1E40AF]" data-testid="cta-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Reveal>
              <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">Ready to Integrate?</h2>
              <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
                Get started with the StayMii API and automate e-invoice compliance for your hospitality platform.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact" className="group px-8 py-4 bg-white text-[#2563EB] rounded-xl font-semibold text-lg hover:shadow-2xl hover:shadow-white/30 transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2" data-testid="cta-request-btn">
                  Request API Access <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a href="mailto:developer@staymii.com" className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl font-semibold text-lg hover:bg-white/20 transition-all border border-white/30 flex items-center justify-center gap-2" data-testid="cta-contact-btn">
                  <Mail className="h-5 w-5" /> Contact Developers
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═══════ FOOTER ═══════ */}
        <LandingFooter />
      </div>
    </>
  );
};

export default Developers;
